import type { LatLon } from "@radar-sim/schema";
import {
  bearingDeg,
  distanceNm,
  normaliseBearing,
  projectPosition,
} from "../geodesy.js";
import type { BehaviourImpl, BehaviourRuntime } from "./types.js";

export interface RaceFleetParams {
  /** Course polyline, lap returns to course[0] when reached. */
  course: ReadonlyArray<LatLon>;
  speedKnots: number;
  /** Starting position along the course as fraction 0..1 of total
   *  course length. */
  progress: number;
  /** Lateral offset perpendicular to the course direction (positive
   *  = starboard of course bearing). */
  lateralOffsetNm: number;
}

const SECONDS_PER_HOUR = 3600;

/**
 * Race-fleet behaviour. Each vessel follows a shared polyline at a
 * constant speed, with optional lateral scatter so a fleet of yachts
 * doesn't render as a single line of dots. When the vessel reaches
 * the end of the course, it wraps back to course[0] for the next
 * lap (suitable for round-the-island, regatta laps, etc.).
 *
 * Determinism: pure function of params + simSeconds. No internal
 * state, no RNG.
 */
export const RaceFleet: BehaviourImpl<RaceFleetParams> = {
  name: "race-fleet",
  version: "1.0.0",
  init({ params, spawnSimSeconds }): BehaviourRuntime {
    // Pre-compute leg lengths in nm + cumulative distance.
    const legLengths: number[] = [];
    let totalNm = 0;
    for (let i = 0; i < params.course.length - 1; i++) {
      const d = distanceNm(params.course[i]!, params.course[i + 1]!);
      legLengths.push(d);
      totalNm += d;
    }
    if (totalNm <= 0) {
      // Degenerate course — return a stationary runtime at the
      // starting waypoint to avoid divide-by-zero.
      return {
        poseAt() {
          return { position: { ...params.course[0]! }, cog: 0, sog: 0 };
        },
      };
    }
    const startDistNm = params.progress * totalNm;
    return {
      poseAt(simSeconds) {
        const elapsedSec = Math.max(0, simSeconds - spawnSimSeconds);
        const elapsedHours = elapsedSec / SECONDS_PER_HOUR;
        const travelledNm = elapsedHours * params.speedKnots;
        // Wrap so the vessel laps continuously.
        let along = (startDistNm + travelledNm) % totalNm;
        if (along < 0) along += totalNm;
        // Walk the legs to find which segment we're on.
        let i = 0;
        let distLeft = along;
        while (i < legLengths.length - 1 && distLeft > legLengths[i]!) {
          distLeft -= legLengths[i]!;
          i++;
        }
        const a = params.course[i]!;
        const b = params.course[i + 1]!;
        const legLen = legLengths[i]!;
        const t = legLen > 0 ? distLeft / legLen : 0;
        const cog = normaliseBearing(bearingDeg(a, b));
        // Linear interpolation in lat/lon for the on-course point,
        // then offset perpendicular to the leg bearing.
        const onCourse = {
          lat: a.lat + (b.lat - a.lat) * t,
          lon: a.lon + (b.lon - a.lon) * t,
        };
        const offsetBearing = normaliseBearing(cog + 90);
        const position =
          params.lateralOffsetNm === 0
            ? onCourse
            : projectPosition(onCourse, offsetBearing, params.lateralOffsetNm);
        return { position, cog, sog: params.speedKnots };
      },
    };
  },
};
